import React, { useState, useEffect, useRef } from "react";
import PLinea from "../img/Boton_1.webp";
import ventanilla from "../img/Boton_2.webp";
import "./FormaPago.css";
import { Redirect, useHistory } from "react-router-dom";
import generarLineaCaptura from "../utilities/GenerarLineaCaptura";
import { usePagoVentanilla } from "../hooks/usePagoVentanilla";
import { PAGOS_API_URL } from "../config";
import { plantelDeMatricula } from "../utilities/plantel";
import { evaluarRespuestaVCE } from "../utilities/resultadoPagoLinea";
import { asegurarVersionVigenteAlumno } from "../utilities/versionApp";
import ResultadoPago, { dinero } from "./ResultadoPago";

const FormaPago = ({ alumno, datospago, tablaConceptos }) => {
  const history = useHistory();
  const { cargandoPdf, generarFolioTPV, guardarIntencionBD, descargarPdf } = usePagoVentanilla(alumno);
  
  // Resultado del pago en linea (null = todavia se esta eligiendo la forma de pago)
  const [resultado, setResultado] = useState(null);
  const mostrar = (tipo, mensaje, extra = {}) => setResultado({ tipo, mensaje, ...extra });
  const [loading, setLoading] = useState(false);
  const [conceptosProcesados, setConceptosProcesados] = useState([]);
  const [intencionGuardada, setIntencionGuardada] = useState(false);
  // Referencias de pago en linea que ya se guardaron (cada intento de pago genera una nueva);
  // evita guardar dos veces el mismo pago sin bloquear un reintento.
  const referenciasEnLineaGuardadas = useRef(new Set());
  
  // Orden de pago de ventanilla: null = aun no se pide; "generando" | "lista" | "error"
  const [ordenVentanilla, setOrdenVentanilla] = useState(null);
  const [folioRegistro] = useState("");
  const [fechaVencimiento, setFechaVencimiento] = useState("");

  // Un solo paso: al elegir ventanilla se registra la orden (solo la primera vez) y se descarga el PDF.
  // Antes habia que dar clic en la imagen y despues en un boton verde "Descargar orden de pago".
  const handleOnClickVentanilla = async () => {
    if (ordenVentanilla === "generando" || cargandoPdf || conceptosProcesados.length === 0) return;
    setOrdenVentanilla("generando");

    let tabla = conceptosProcesados;
    if (!intencionGuardada) {
      // 1. Generamos el folio
      const nuevoFolio = generarFolioTPV();

      // 2. INYECTAMOS el folio en cada fila de la tabla
      tabla = conceptosProcesados.map(item => ({
        ...item,
        folioTPV: item.folioTPV || nuevoFolio // Respeta si ya trae uno, si no, le asigna el nuevo
      }));

      // 3. Actualizamos la tabla para que el componente PDF la lea correctamente
      setConceptosProcesados(tabla);

      // 4. Guardamos en la base de datos
      const importePDF = tabla.reduce((sum, item) => sum + Number(item.precio), 0);
      const refBanortePDF = tabla[0].referencias.Banorte;
      const refBbvaPDF = tabla[0].referencias.BBVA;

      await guardarIntencionBD(tabla, importePDF, refBanortePDF, refBbvaPDF, nuevoFolio, "VENTANILLA");

      // 5. Las siguientes descargas ya no vuelven a registrar la orden
      setIntencionGuardada(true);
    }

    // 6. Descargamos el PDF con la tabla que ya trae el folio (el estado todavia no se actualiza aqui)
    const descargado = await descargarPdf(tabla, folioRegistro, fechaVencimiento);
    setOrdenVentanilla(descargado ? "lista" : "error");
  };

  const totalAPagar = conceptosProcesados.reduce((sum, item) => sum + Number(item.precio), 0);

  const pagoBanorte = async (importe, referencia) => {
    let xObjEnc;
    // La API usa la matricula como "Dato de compra" (customerRef4) del voucher digital de Banorte;
    // solo la matricula (14 caracteres) porque Banorte agrega "VCE" y con mas caracteres el pago
    // falla. datoCompra (nombres de los conceptos) tambien se manda, pero hoy la API lo ignora.
    const datoCompra = conceptosProcesados.map((item) => item.concepto).filter(Boolean).join(", ");
    const body = { referencia, importe, datoCompra, matricula: String(alumno.codigo || "") };
    setLoading(true);

    // Antes de abrir la ventana de Banorte: si la pagina quedo vieja (se publico una version nueva mientras
    // estaba abierta, o el navegador guardo una copia), se avisa y se recarga; una pagina vieja podria guardar
    // como pagado un cobro que Banorte rechazo.
    if (!(await asegurarVersionVigenteAlumno())) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${PAGOS_API_URL}/referenciaBanorte`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
      });

      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }
      // Sin datos para la ventana de Banorte no se abre (antes se abria vacia): 503 = el cifrador no esta
      // disponible; 400 = la solicitud no paso la validacion del servicio; 200 con {} = servicio anterior.
      if (!response.ok || !data || !data.data) {
        mostrar("reintentar", response.status === 400
          ? "No se pudo preparar el pago. No se hizo ningún cobro; recarga la página e intenta de nuevo."
          : "El servicio de pagos no está disponible en este momento. No se hizo ningún cobro; intenta de nuevo en un minuto.");
        setLoading(false);
        return;
      }
      xObjEnc = data.data;
      // vi/salt/passphrase: llaves con las que se descifra la respuesta de Banorte en onSuccess
      const clavesVCE = { vi: data.vi, salt: data.salt, passphrase: data.passphrase };

      window.Payment.startPayment({
        Params: xObjEnc,
        onClosed: () => setLoading(false),
        onError: (response) => {
          mostrar("reintentar", `${response.message || "Error en el pago"}. No se hizo ningún cobro.`);
          setLoading(false);
        },
        onSuccess: (response) => {
          setLoading(false);
          // La ventana llama a onSuccess aunque Banorte haya declinado el cobro: solo se
          // guarda el pago si el resultado es "aprobado" (ver utilities/resultadoPagoLinea.js).
          const evaluacion = evaluarRespuestaVCE(response, clavesVCE);
          if (evaluacion.desconocido) {
            // No se sabe si Banorte cobro: NO se guarda nada (el servidor tampoco lo aceptaria sin autorizacion).
            // Si el cobro fue real, se da de alta desde el modulo de Conciliacion.
            console.warn("No se pudo confirmar el resultado del pago con Banorte; no se guarda:", evaluacion.error || response);
            mostrar("advertencia", "No pudimos confirmar tu pago. Si tu banco te hizo el cargo, conserva tu comprobante y repórtalo en tu plantel; no vuelvas a pagar hasta confirmarlo.");
            return;
          }
          if (!evaluacion.aprobado) {
            mostrar("rechazado", `El pago no fue aprobado (${evaluacion.motivo}). No se realizó ningún cobro; intenta de nuevo o con otra tarjeta.`);
            return;
          }
          mostrar("aprobado", "Pago exitoso", { autorizacion: evaluacion.autorizacion, importe, conceptos: conceptosProcesados, fecha: new Date() });
          if (!referenciasEnLineaGuardadas.current.has(referencia)) {
            referenciasEnLineaGuardadas.current.add(referencia);
            // La autorizacion de Banorte viaja con el pago: el servidor no guarda un pago en linea sin ella.
            Promise.resolve(guardarIntencionBD(conceptosProcesados, importe, referencia, null, null, "EN_LINEA", evaluacion.autorizacion)).then((guardado) => {
              if (!guardado) {
                mostrar("advertencia", `Banorte aprobó tu pago (autorización ${evaluacion.autorizacion}) pero no se pudo registrar en el sistema. Conserva tu comprobante y avisa en tu plantel con este número de autorización.`);
              }
            });
          }
        },
        onCancel: () => setLoading(false),
      });

    } catch (err) {
      console.error("Error crítico de red o API:", err);
      mostrar("reintentar", "No se pudo conectar con el servidor de pagos. Revisa tu conexión a internet e intenta de nuevo.");
      setLoading(false);
    }
  };

  const completeTable = () => {
    const matriculaStr = String(alumno.codigo);
    const plantelOficialAlumno = plantelDeMatricula(matriculaStr);

    let tablaCopia = JSON.parse(JSON.stringify(tablaConceptos));

    const yaVienenAgrupados = tablaCopia.every(
      (item) => item.referencias && item.referencias.Banorte && item.referencias.Banorte.length > 20
    );

    if (!yaVienenAgrupados) {
      const todasLasClaves = tablaCopia.map((item) => item.clave);
      const montoTotalBolsa = tablaCopia.reduce((sum, item) => sum + Number(item.precio), 0);

      const datosRefUnicaM97 = generarLineaCaptura(
        plantelOficialAlumno,
        todasLasClaves,
        matriculaStr,
        montoTotalBolsa
      );

      setFechaVencimiento(datosRefUnicaM97.banorte.fechaVencimientoStr);

      for (let i = 0; i < tablaCopia.length; i++) {
        tablaCopia[i].id = i + 1;
        tablaCopia[i].referencias = {
          Banorte: datosRefUnicaM97.banorte.lineaDeCaptura,
          BBVA: datosRefUnicaM97.bancomer.lineaDeCaptura
        };
      }
    } else {
      for (let i = 0; i < tablaCopia.length; i++) {
        tablaCopia[i].id = i + 1;
      }
    }
    const fechaHeredada =
      datospago?.detallesCompletos?.banorte?.fechaVencimientoStr ||
      datospago?.fechaVencimiento;

    if (!fechaVencimiento && fechaHeredada) {
      setFechaVencimiento(fechaHeredada);
    }
    setConceptosProcesados(tablaCopia);
  };

  useEffect(() => {
    completeTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tablaConceptos]);

  return alumno.codigo === "" ? (
    <Redirect to="/login" />
  ) : loading ? (
    <div className="loading_spinner">
      <div className="lds-ring">
        <div></div><div></div><div></div><div></div>
      </div>
    </div>
  ) : resultado ? (
    <div className="FormaPago container">
      <ResultadoPago resultado={resultado} onReintentar={() => setResultado(null)} onInicio={() => history.push("/tipo")} />
    </div>
  ) : (
    <div className="FormaPago container">
      <button className="FormaPago-btn-regresar" onClick={() => history.goBack()}>
        &#8592; Regresar
      </button>

      {/* Antes de elegir, el alumno ve que va a pagar y cuanto */}
      {conceptosProcesados.length > 0 && (
        <section className="FormaPago-resumen" aria-label="Resumen de tu pago">
          <h3 className="FormaPago-resumen-titulo">Vas a pagar</h3>
          <ul className="FormaPago-resumen-lista">
            {conceptosProcesados.map((item) => (
              <li key={item.id || item.clave}>
                <span>{item.concepto}</span>
                <span className="FormaPago-resumen-precio">{dinero(item.precio)}</span>
              </li>
            ))}
          </ul>
          <p className="FormaPago-resumen-total">
            <span>Total</span>
            <span>{dinero(totalAPagar)}</span>
          </p>
        </section>
      )}

      <h2>Elige tu Forma de pago</h2>
      <div className="FormaPago-flex">
        
        <div className="FormaPago-linea" onClick={() => {
            const importeBruto = conceptosProcesados.length > 0
              ? conceptosProcesados.reduce((sum, item) => sum + Number(item.precio), 0)
              : datospago.importe;

            const importeParaBanco = Number(importeBruto).toFixed(2);
            const referenciaCompleta = conceptosProcesados.length > 0 && conceptosProcesados[0].referencias?.Banorte
              ? conceptosProcesados[0].referencias.Banorte
              : datospago.referencia;

            const referenciaOficial26 = referenciaCompleta ? referenciaCompleta.substring(0, 26) : "";
            const sufijoTiempo = Date.now().toString().slice(-4);
            const referenciaParaBanco = referenciaOficial26 ? referenciaOficial26 + sufijoTiempo : "";

            pagoBanorte(importeParaBanco, referenciaParaBanco);
          }}
        >
          <div className="contenedor-imagen">
            <input className="FormaPago-linea-img" type="image" src={PLinea} alt="Pago en línea" />
          </div>
          <p>Tarjetas de crédito y débito <br /> (VISA y MasterCard) de cualquier banco.</p>
        </div>

        <div className="FormaPago-referencia">
          <div className="contenedor-imagen">
            <input
              className="FormaPago-ventanilla-img"
              type="image"
              src={ventanilla}
              onClick={handleOnClickVentanilla}
              alt="Pago en ventanilla"
            />
          </div>
          <p>Pago en ventanilla de banco, <br /> practicajas y aplicación móvil</p>

          {ordenVentanilla === "generando" || cargandoPdf ? (
            <p className="FormaPago-orden-estado" role="status">Generando tu orden de pago...</p>
          ) : ordenVentanilla === "lista" ? (
            <div className="FormaPago-orden-estado FormaPago-orden-estado--lista" role="status">
              <p>✅ Tu orden de pago se descargó. Búscala en las descargas de tu dispositivo.</p>
              <button type="button" className="FormaPago-orden-boton" onClick={handleOnClickVentanilla}>
                Descargar de nuevo
              </button>
            </div>
          ) : ordenVentanilla === "error" ? (
            <div className="FormaPago-orden-estado FormaPago-orden-estado--error" role="status">
              <p>No se pudo generar tu orden de pago. Revisa tu conexión e intenta de nuevo.</p>
              <button type="button" className="FormaPago-orden-boton" onClick={handleOnClickVentanilla}>
                Intentar de nuevo
              </button>
            </div>
          ) : null}

        </div>
      </div>
    </div>
  );
};

export default FormaPago;